/**
 * The retry policy for a user's download requests (the spec section cut, the section export):
 * a request is sent again only after a transient failure, at most {@link TRANSIENT_RETRIES}
 * times, like `postProductEvents`. Everything else rejects at once with the request's own
 * error: a refusal (any other 4xx, including a 409 that carries the server's verdict), a 500,
 * a cancellation.
 */

import { isAxiosError, isCancel } from "axios";
import pRetry from "p-retry";

import { responseHeader } from "../utils/responseHeaders";

export const TRANSIENT_RETRIES = 2;

const TRANSIENT_STATUSES = new Set([429, 502, 503, 504]);
const BASE_DELAY_MS = 1_000;
/** A longer Retry-After is waited only this long: the user is watching the download. */
const MAX_RETRY_AFTER_MS = 10_000;

/**
 * A failure worth sending again: an axios request that got no response (a network error, a
 * timeout) and was not cancelled, or one answered 429, 502, 503 or 504.
 */
function isTransientFailure(error: unknown): boolean {
    if (!isAxiosError(error) || isCancel(error)) return false;
    const status = error.response?.status;
    return status === undefined || TRANSIENT_STATUSES.has(status);
}

function retryAfterMilliseconds(error: unknown): number {
    const value = isAxiosError(error)
        ? responseHeader(error.response?.headers, "Retry-After")
        : undefined;
    if (!value) return 0;
    const seconds = Number(value);
    const milliseconds = Number.isFinite(seconds)
        ? seconds * 1_000
        : Date.parse(value) - Date.now();
    return Number.isFinite(milliseconds) && milliseconds > 0
        ? Math.min(milliseconds, MAX_RETRY_AFTER_MS)
        : 0;
}

function backoffMilliseconds(retryIndex: number): number {
    return BASE_DELAY_MS * 2 ** retryIndex * (1 + Math.random());
}

function wait(milliseconds: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, milliseconds));
}

/** Carries an attempt's own error, whatever it is, through p-retry: p-retry rejects a thrown
 *  non-`Error` as a TypeError, and its `AbortError` would wrap one in a new `Error`. */
class FailedAttempt extends Error {
    constructor(readonly failure: unknown) {
        super("Request attempt failed");
        this.name = "FailedAttempt";
    }
}

/**
 * Run `request`, sending it again after a transient failure ({@link isTransientFailure}) up
 * to {@link TRANSIENT_RETRIES} times: after 1–2 s, then 2–4 s, or the server's Retry-After
 * when that is longer (capped at 10 s). Rejects with the last attempt's own error.
 */
export async function withTransientRetry<T>(request: () => Promise<T>): Promise<T> {
    try {
        return await pRetry(
            async () => {
                try {
                    return await request();
                } catch (failure) {
                    const attempt = new FailedAttempt(failure);
                    throw isTransientFailure(failure) ? attempt : new pRetry.AbortError(attempt);
                }
            },
            {
                retries: TRANSIENT_RETRIES,
                // p-retry owns the attempt loop; this hook owns its single delay, so the backoff
                // and a Retry-After combine rather than stack.
                minTimeout: 0,
                maxTimeout: 0,
                onFailedAttempt: async (error) => {
                    if (error.retriesLeft === 0) return;
                    const retryAfter =
                        error instanceof FailedAttempt ? retryAfterMilliseconds(error.failure) : 0;
                    await wait(Math.max(backoffMilliseconds(error.attemptNumber - 1), retryAfter));
                },
            },
        );
    } catch (error) {
        throw error instanceof FailedAttempt ? error.failure : error;
    }
}
